import { useEffect, useMemo } from "react";
import { usePageTitle } from "../../../hooks/usePageTitle";
import {
  Box,
  Stack,
  Typography,
  Tooltip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  QueryStatus,
  useExplorerStore,
  ExplorerStoreProvider,
} from "../../../data/ExplorerStore";
import { ExplorerEnvironment } from "../../../data/Cache";
import QueryBuilder from "./QueryBuilder";
import { useLocation } from "react-router-dom";
import ExplorerDataGrid from "../../Elements/ExplorerDataGrid/ExplorerDataGrid";
import ExplorerErrorCard from "./ExplorerErrorCard";
import QueryResultNotices from "../../Elements/QueryResultNotices/QueryResultNotices";
import { HelpOutlineOutlined } from "@mui/icons-material";
import { buildValueLookup } from "../../Elements/ExplorerDataGrid/VideoCellHandler";

function ExplorerContent() {
  const data = useExplorerStore((state) => state.data);
  const explorerStatus = useExplorerStore((state) => state.explorerStatus);
  const initExplorer = useExplorerStore((state) => state.initExplorer);
  const isLoading = useExplorerStore(
    (state) => state.queryStatus === QueryStatus.Requesting,
  );
  const savedQuery = useExplorerStore((state) => state.savedQuery);
  const error = useExplorerStore((state) => state.error);
  const queryContext = useExplorerStore((state) => state.queryContext);
  const recoded = useExplorerStore((state) => state.recoded);
  const seamsCrossed = useExplorerStore((state) => state.seamsCrossed);
  const pinnedSource = useExplorerStore((state) => state.pinnedSource);
  const api = useExplorerStore((state) => state.api);
  const storeEnvironment = useExplorerStore((state) => state.environment);
  const filters = useExplorerStore((state) => state.filters);
  const playerFilterOptions = useExplorerStore(
    (state) => state.playerFilterOptions,
  );
  const location = useLocation();

  const valueLookup = useMemo(
    () => buildValueLookup(filters, playerFilterOptions?.values),
    [filters, playerFilterOptions?.values],
  );

  const environmentLabel =
    storeEnvironment === ExplorerEnvironment.NBA ? "NBA" : "Draft";

  usePageTitle(
    savedQuery?.NAME
      ? `${savedQuery.NAME} - ${environmentLabel} Explorer`
      : `${environmentLabel} Explorer`,
  );

  useEffect(() => {
    if (explorerStatus === QueryStatus.None) {
      // Read query ID from ?q= param, falling back to #hash for old bookmarks
      const params = new URLSearchParams(location.search);
      const queryId = params.get("q") || location.hash.substring(1);
      initExplorer(queryId);
    }
  }, [explorerStatus, initExplorer, location]);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const pendingTableState = useExplorerStore(
    (state) => state.pendingTableState,
  );
  const searchParams = new URLSearchParams(location.search);
  // Table state from URL (shared links) or store (loaded saved query)
  const initialTableState =
    searchParams.get("ts") ?? pendingTableState ?? undefined;

  const filterNames = useMemo(
    () => (filters ? Object.keys(filters) : []),
    [filters],
  );

  return (
    <Box
      sx={{
        px: { xs: 0.5, sm: 1, md: 3 },
        maxWidth: "xl",
        mx: "auto",
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Stack
        className="explorer-controls"
        direction="row"
        py={{ xs: 0.5, sm: 1 }}
        alignItems="center"
        justifyContent="space-between"
        flexShrink={0}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={0.5}
          sx={{ minWidth: 0, flex: 1 }}
        >
          <Typography
            variant={isMobile ? "h6" : "h4"}
            sx={{
              textTransform: "capitalize",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {savedQuery?.NAME ?? ""}
          </Typography>
          {savedQuery?.DESCRIPTION && (
            <Tooltip title={savedQuery?.DESCRIPTION || ""}>
              <HelpOutlineOutlined fontSize="small" />
            </Tooltip>
          )}
        </Stack>
        <Stack
          direction="row"
          alignItems="center"
          spacing={0.5}
          justifyContent="flex-end"
          flexShrink={0}
        >
          <QueryBuilder />
        </Stack>
      </Stack>
      {!error && !isLoading && (
        <QueryResultNotices
          api={api}
          seamsCrossed={seamsCrossed}
          recoded={recoded}
          resolution={queryContext?.resolution}
          pinnedSource={pinnedSource}
        />
      )}
      <Box sx={{ flex: 1, minHeight: 0, position: "relative" }}>
        <ExplorerDataGrid
          data={data}
          loading={isLoading}
          isMobile={isMobile}
          queryContext={queryContext}
          environment={storeEnvironment}
          valueLookup={valueLookup}
          initialTableState={initialTableState}
          filterNames={filterNames}
        />
        {error && <ExplorerErrorCard error={error} />}
      </Box>
    </Box>
  );
}

export default function Explorer() {
  return (
    <ExplorerStoreProvider>
      <ExplorerContent />
    </ExplorerStoreProvider>
  );
}
